import React from "react";
import { PieChart, Pie, Cell, Legend } from "recharts";
import {
  formatNumber,
  formatPercentage,
} from "@signolteam/shared/units-of-measure";
import {
  getBehaviourColourForPosition,
  SIGNOL_COLOURS,
} from "@signolteam/shared/colours";
import { useDCContext, useAuthContext } from "@src/context";
import { useManagerContext } from "@src/context/managerContext";
import ManagerHooks from "@src/context/hooks/managerHooks";
import { AppUtils, SignolUtils } from "@src/utils";
import { Loader, Tooltip } from "@src/components/atoms";
import PieChartLib from "@src/components/graphs/PieChart";
import PropTypes from "prop-types";

/** A breakdown of events for a specific behaviour, by status (success, fail, excluded, faulty).
 * Known as many things: Behaviour Results / Opportunity Overview / Data Overview / Status Counts
 */
const PerformanceDataCountsAndPieChartCard = () => {
  const { profile, selectedBehaviour, selectedPeriod, selectedTeam } =
    useManagerContext();
  const { getDynamicReplacement } = useDCContext();
  const auth = useAuthContext();
  const {
    data: {
      behaviourStatusCounts,
      countBehavioursTotal,
      countBehavioursPerformable,
    } = {},
    isPending: isPendingDataOverview,
    error: errorDataOverview,
  } = ManagerHooks.useGetBehaviourDataOverviewStatusCounts(
    auth.activeRole,
    selectedBehaviour.behaviourKey,
    selectedTeam?.id,
    selectedPeriod,
    AppUtils.endOfMonthIfValidDate(selectedPeriod),
  );

  const bvrColor = getBehaviourColourForPosition(selectedBehaviour.order);

  const pieChartData = [
    {
      name: "Total Successful",
      value: behaviourStatusCounts?.countSuccess,
      colour: bvrColor.codes[500],
    },
    {
      name: "Total Unsuccessful",
      value: behaviourStatusCounts?.countUnsuccessful,
      colour: bvrColor.codes[400],
    },
    {
      name: "Excluded Events",
      value: behaviourStatusCounts?.countExcluded,
      colour: SIGNOL_COLOURS.neutral[300],
    },
    {
      name: "Faulty Events",
      value: behaviourStatusCounts?.countBadMissingData,
      colour: SIGNOL_COLOURS.neutral[400],
    },
  ].map((x) => ({
    ...x,
    percentage:
      typeof x.value === "number"
        ? formatPercentage("0-1", x.value / countBehavioursTotal).asString
        : undefined,
  }));
  const dcVars = {
    EVENT_NAME_PLURAL: profile.customer.preferredEventNamePlural,
    EVENT_NAME: profile.customer.preferredEventName,
  };
  const dataOverviewCardTitle = getDynamicReplacement(
    "fuel_manager__behaviour__data_overview_card__title",
    dcVars,
  );
  const dataOverviewSuccessful = getDynamicReplacement(
    "fuel_manager__behaviour__data_overview_card__successful_text",
    dcVars,
  );
  const dataOverviewUnsuccessful = getDynamicReplacement(
    "fuel_manager__behaviour__data_overview_card__unsuccessful_text",
    dcVars,
  );
  const dataOverviewExcluded = getDynamicReplacement(
    "fuel_manager__behaviour__data_overview_card__excluded_text",
    dcVars,
  );
  const dataOverviewFaulty = getDynamicReplacement(
    "fuel_manager__behaviour__data_overview_card__faulty_text",
    dcVars,
  );
  const dataOverviewTotalOpportunities = getDynamicReplacement(
    "fuel_manager__behaviour__data_overview_card__total_opportunities_text",
    dcVars,
    "eligible events",
  );
  const opportunitiesTooltipContent = [
    getDynamicReplacement(
      "fuel_manager__tooltip__behaviour_performance_opportunities",
      dcVars,
    ),
  ];

  return (
    <div className={`bg-${bvrColor.name}-500 pl-1 rounded-xl shadow-md`}>
      <div
        className="h-full bg-white rounded-xl flex flex-col justify-between overflow-hidden"
        style={{ minHeight: "200px", minWidth: "310px" }}
      >
        {isPendingDataOverview ? (
          <Loader size="2xl" />
        ) : errorDataOverview ? (
          <div className="text-red-500">Error fetching overview data...</div>
        ) : (
          <div className="h-full relative">
            <div className="text-neutral-500 text-sm font-medium mt-6 ml-6">
              {dataOverviewCardTitle}
            </div>
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute right-3 top-3"
            >
              <Tooltip
                content={opportunitiesTooltipContent}
                direction={"down-left"}
                absolute={false}
              />
            </div>
            <div className="flex items-center ml-6">
              <div className="mr-3">
                <PieChartLib data={pieChartData} hideTooltips />
              </div>
              <div className="leading-8 hidden xs:flex">
                <div className="flex flex-col items-end mr-2">
                  <span className={`text-3xl text-${bvrColor.name}-500`}>
                    {formatNumber(
                      behaviourStatusCounts?.countSuccess,
                    ).toString()}
                  </span>
                  <span className={`text-3xl text-${bvrColor.name}-400`}>
                    {formatNumber(
                      behaviourStatusCounts?.countUnsuccessful,
                    ).toString()}
                  </span>
                </div>
                <div className="flex flex-col">
                  <div className="flex items-end h-9">
                    <span className="text-base text-black mr-2">
                      {dataOverviewSuccessful}
                    </span>
                  </div>
                  <div className="flex items-end h-9">
                    <span className="text-base text-black mr-2">
                      {dataOverviewUnsuccessful}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col leading-8 xs:hidden">
                <span>
                  <span className={`text-3xl text-${bvrColor.name}-500`}>
                    {formatNumber(
                      behaviourStatusCounts?.countSuccess,
                    ).toString()}
                  </span>
                  <span className="text-base text-black ml-2">
                    {dataOverviewSuccessful}
                  </span>
                </span>
                <span>
                  <span className={`text-3xl text-${bvrColor.name}-400`}>
                    {formatNumber(
                      behaviourStatusCounts?.countUnsuccessful,
                    ).toString()}
                  </span>
                  <span className="text-base text-black ml-2">
                    {dataOverviewUnsuccessful}
                  </span>
                </span>
              </div>
            </div>
            <div className="justify-stretch w-full mt-6 hidden md:flex space-x-1">
              <div
                className={`bg-${bvrColor.name}-100 py-3 px-6 grow space-x-1`}
              >
                <span className={`text-${bvrColor.name}-800`}>
                  {formatNumber(countBehavioursPerformable).toString()}
                </span>
                <span className={`text-${bvrColor.name}-500 text-sm`}>
                  {dataOverviewTotalOpportunities}
                </span>
              </div>
              <div className="bg-neutral-400 py-3 px-6 space-x-1 text-white">
                <span>
                  {formatNumber(
                    behaviourStatusCounts?.countExcluded,
                  ).toString()}
                </span>
                <span className="text-sm">{dataOverviewExcluded}</span>
              </div>
              <div className="bg-neutral-500 py-3 px-6 space-x-1 text-white">
                <span>
                  {formatNumber(
                    behaviourStatusCounts?.countBadMissingData,
                  ).toString()}
                </span>
                <span className="text-sm">{dataOverviewFaulty}</span>
              </div>
            </div>
            <div className="flex flex-wrap justify-stretch w-full mt-6 md:hidden">
              <div
                className={`bg-${bvrColor.name}-100 py-3 px-6 w-full flex justify-center items-center gap-1`}
              >
                <span className={`text-${bvrColor.name}-800`}>
                  {formatNumber(countBehavioursPerformable).toString()}
                </span>
                <span className={`text-${bvrColor.name}-500 text-sm`}>
                  {dataOverviewTotalOpportunities}
                </span>
              </div>
              <div className="bg-neutral-400 py-3 px-6 w-1/2 flex justify-center items-center gap-1 text-white">
                <span>
                  {formatNumber(
                    behaviourStatusCounts?.countExcluded,
                  ).toString()}
                </span>
                <span className="text-sm">{dataOverviewExcluded}</span>
              </div>
              <div className="bg-neutral-500 py-3 px-6 w-1/2 flex justify-center items-center gap-1 text-white">
                <span>
                  {formatNumber(
                    behaviourStatusCounts?.countBadMissingData,
                  ).toString()}
                </span>
                <span className="text-sm">{dataOverviewFaulty}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export const performanceDataCountsAndPieChartCardControls = {};

PerformanceDataCountsAndPieChartCard.propTypes =
  AppUtils.getPropTypeFromControls(
    performanceDataCountsAndPieChartCardControls,
  );
export default PerformanceDataCountsAndPieChartCard;
